import { useEffect } from 'react'
import {
  isPlateImagingObservation,
  type PlatePage_ObservationHistory,
} from '~/api/backend/pages/platePageAPI'
import { warmImageURL } from '~/utils/images'
import { prefersLargePlateSprite } from './plateSpriteSizing'

/**
 * Preloading for PlatePage adjacent observations
 *
 * Similar to WellPage preloading but simpler - only preloads observation sprites,
 * not adjacent wells (since PlatePage doesn't navigate between wells).
 *
 * Key differences from WellPage:
 * - Preloads single sprite image per observation (not 16 individual site images)
 * - Only navigates through time (observations), not space (wells)
 * - Uses useExtractedPlateSprite instead of useLargeImageBlobUrls
 *
 * Performance impact:
 * - Current observation loads first with full bandwidth
 * - Adjacent observations start preloading immediately after current sprite finishes (no delay)
 * - Navigation becomes instant (~0ms from cache vs ~1000ms network)
 */

/**
 * Helper function to preload sprite image for a plate observation
 * Warms browser HTTP cache so subsequent loads are instant
 *
 * Strategy: Preload v2 sprite based on display resolution, fall back to v1
 */
async function preloadObservationSprite(observation: PlatePage_ObservationHistory) {
  // Only preload if this is an imaging observation with a sprite
  if (!isPlateImagingObservation(observation) || !observation.sprite) {
    return
  }

  // Select sprite URL based on display resolution (matching PlateHero logic)
  const spriteUrlV2 = prefersLargePlateSprite()
    ? (observation.sprite.imageUrlLarge ?? observation.sprite.imageUrlStandard)
    : observation.sprite.imageUrlStandard
  const spriteUrl = spriteUrlV2 ?? observation.sprite.imageUrl

  if (!spriteUrl) return

  const warmedUrl = warmImageURL(spriteUrl)

  // Fetch sprite to warm browser cache
  // useExtractedPlateSprite will fetch from cache when user navigates
  // Errors are intentionally ignored - preloading is best-effort optimization
  await fetch(warmedUrl, { cache: 'force-cache' }).catch(() => {})
}

/**
 * Hook to preload sprite images for adjacent plate observations (prev/next in timeline)
 *
 * Uses adaptive timing via the `imagesLoaded` flag - starts preloading immediately
 * when the current observation's sprite finishes loading. This ensures:
 * - Current observation gets full bandwidth (no competition)
 * - Preloading starts as soon as it's safe (no fixed delays)
 * - Adjacent observations load instantly from cache when user navigates
 *
 * Preloads prev and next observations in parallel using Promise.all() for faster
 * completion (~3-5s total instead of 6-10s sequential).
 *
 * @param observations - Array of all plate observations sorted by timestamp
 * @param currentIndex - Index of currently displayed observation
 * @param imagesLoaded - Whether current observation's sprite has finished loading
 */
export function usePreloadAdjacentPlateObservations(
  observations: PlatePage_ObservationHistory[],
  currentIndex: number,
  imagesLoaded: boolean,
) {
  useEffect(() => {
    if (!imagesLoaded || observations.length === 0) return

    let cancelled = false

    const preloadObservations = async () => {
      if (cancelled) return

      const adjacentObservations: PlatePage_ObservationHistory[] = []

      // Preload previous observation
      if (currentIndex > 0) {
        const prevObs = observations[currentIndex - 1]
        if (prevObs && isPlateImagingObservation(prevObs) && prevObs.sprite) {
          adjacentObservations.push(prevObs)
        }
      }

      // Preload next observation
      if (currentIndex < observations.length - 1) {
        const nextObs = observations[currentIndex + 1]
        if (nextObs && isPlateImagingObservation(nextObs) && nextObs.sprite) {
          adjacentObservations.push(nextObs)
        }
      }

      // Preload sprite images in parallel (observations are high priority)
      // Start immediately when current page sprite finishes (no delay)
      await Promise.all(adjacentObservations.map(obs => preloadObservationSprite(obs)))
    }

    preloadObservations().catch(() => {
      // Silently fail - preloading is best-effort optimization
    })

    return () => {
      cancelled = true
    }
  }, [observations, currentIndex, imagesLoaded])
}
